import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, JWT_KEY } from "../lib/nailApi";

const AuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    const jwt = typeof window !== "undefined" ? localStorage.getItem(JWT_KEY) : null;
    if (!jwt) {
      setAdmin(null);
      setChecking(false);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setAdmin(data);
      setChecking(false);
      return data;
    } catch (_err) {
      try { localStorage.removeItem(JWT_KEY); } catch (_e) { /* ignore */ }
      setAdmin(null);
      setChecking(false);
      return null;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/admin/login", { email, password });
    localStorage.setItem(JWT_KEY, data.access_token);
    // Seed legacy session key so existing admin pages (which gate UI on this) don't show their own login form.
    // Backend will accept JWT Bearer (sent by interceptor) — the X-Admin-Token sent alongside is just a marker.
    sessionStorage.setItem("nailEvent.adminToken", data.access_token);
    setAdmin({ email: data.email, role: data.role });
    return data;
  }, []);

  const logout = useCallback(() => {
    try { localStorage.removeItem(JWT_KEY); } catch (_e1) { /* ignore */ }
    try { sessionStorage.removeItem("nailEvent.adminToken"); } catch (_e2) { /* ignore */ }
    setAdmin(null);
  }, []);

  const value = useMemo(() => ({ admin, checking, login, logout, refresh }), [admin, checking, login, logout, refresh]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  return ctx;
}
